/**
 * Local analytics state + GA4 fetches for the Story List ANALYTICS tab.
 *
 * Replaces `storyListEffects.getAnalyticsProperty` / `getAnalyticsData` and the
 * `storyListState.analytics` + `analyticsLoadingStatus` fields (compose-app).
 */

import { transport } from '@opengov/app-core/transport';
import { useCallback, useMemo, useState } from 'react';

import {
  IGAAccountSummariesResponse,
  IGAPropertysDataStreamsResponse,
  IStoryAnalytics,
  IStoryAnalyticsPayload
} from '../../../../types';

const EMPTY_ANALYTICS_DATA: IStoryAnalytics['data'] = {
  dimensionHeaders: [],
  kind: '',
  metricHeaders: [],
  rowCount: 0,
  rows: []
};

async function fetchAnalyticsProperty(
  authHeader: string,
  googleAnalyticsId: string
): Promise<{ propertyId: string } | null> {
  const response: IGAAccountSummariesResponse = await transport
    .url('https://analyticsadmin.googleapis.com/v1beta/accountSummaries')
    .auth(authHeader)
    .get()
    .json();

  for (const accountSummary of response.accountSummaries ?? []) {
    if (!accountSummary.propertySummaries) {
      continue;
    }
    for (const propertySummary of accountSummary.propertySummaries) {
      const propertyValue = propertySummary.property;
      const dataStreamResponse: IGAPropertysDataStreamsResponse = await transport
        .url(`https://analyticsadmin.googleapis.com/v1beta/${propertyValue}/dataStreams`)
        .auth(authHeader)
        .get()
        .json();

      const matchingDataStream = dataStreamResponse.dataStreams?.find(
        (stream) => stream.webStreamData.measurementId === googleAnalyticsId
      );
      if (matchingDataStream) {
        return { propertyId: propertyValue };
      }
    }
  }
  return null;
}

async function fetchAnalyticsReport(payload: IStoryAnalyticsPayload) {
  const { propertyId, authHeader, dateRange, metrics, dimensions } = payload;
  return transport
    .url(`https://analyticsdata.googleapis.com/v1beta/${propertyId}:runReport`)
    .auth(authHeader)
    .json({ dateRanges: [dateRange], dimensions, metrics })
    .post()
    .json();
}

export function useStoryListAnalytics(googleAnalyticsId: string | undefined) {
  const [authHeader, setAuthHeaderState] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [data, setData] = useState<IStoryAnalytics['data']>(EMPTY_ANALYTICS_DATA);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(false);

  const analytics = useMemo<IStoryAnalytics>(
    () => ({ authHeader, propertyId, data }),
    [authHeader, propertyId, data]
  );

  const setAnalyticsAuthHeader = useCallback((header: string) => {
    setAuthHeaderState(header);
    setPropertyId('');
    setData(EMPTY_ANALYTICS_DATA);
  }, []);

  const getAnalyticsProperty = useCallback(
    async (params: { authHeader: string; google_analytics_id: string }) => {
      if (!params.google_analytics_id) {
        return null;
      }
      setIsAnalyticsLoading(true);
      try {
        const result = await fetchAnalyticsProperty(params.authHeader, params.google_analytics_id);
        if (result?.propertyId) {
          setPropertyId(result.propertyId);
        }
        return result;
      } finally {
        setIsAnalyticsLoading(false);
      }
    },
    []
  );

  const getAnalyticsData = useCallback(async (payload: IStoryAnalyticsPayload) => {
    setIsAnalyticsLoading(true);
    try {
      const response = await fetchAnalyticsReport(payload);
      setData(response as IStoryAnalytics['data']);
      return response;
    } catch {
      return null;
    } finally {
      setIsAnalyticsLoading(false);
    }
  }, []);

  const toggleAnalyticsLoading = useCallback((loading: boolean) => {
    setIsAnalyticsLoading(loading);
  }, []);

  return {
    analytics,
    isAnalyticsLoading,
    googleAnalyticsId,
    setAnalyticsAuthHeader,
    getAnalyticsProperty,
    getAnalyticsData,
    toggleAnalyticsLoading
  };
}
